'use client';

/**
 * BiohackerDashboard — bespoke body for the "Biohacker" persona.
 *
 * The quantified-self optimizer opens the app to read their systems, not
 * to play a game. The hero is a BIO-SYSTEMS panel: composite readiness
 * plus the recovery quad (HRV / RHR / Sleep / NEAT) read against each
 * signal's own baseline. Below it sits the optimization stack — the levers
 * they actually tune: cross-domain action points, training-stress balance
 * (CTL/ATL/TSB), cardiac age, and a nutrition-precision line.
 *
 * Degrades gracefully without a wearable: the quad shows "no signal"
 * cells, the hero falls back to the subjective check-in, and a connect
 * prompt appears. No gamification / operations / territory.
 */

import { useEffect, useState } from 'react';
import { Activity, HeartPulse, Moon, Footprints, ArrowRight, Beaker } from 'lucide-react';
import { displayReadiness } from '@/lib/unifiedIntelligence';
import StartWorkoutCard from '@/components/dashboard/StartWorkoutCard';
import ActionPoints from '@/components/metrics-briefing/ActionPoints';
import FormCurveCard from '@/components/metrics-ops/FormCurveCard';
import CardiacAgeCard from '@/components/dashboard/CardiacAgeCard';
import type { FormCurveSummary } from '@/lib/formCurve';
import type { DashboardData } from '@/lib/hooks/useDashboardData';

type Tone = 'good' | 'warn' | 'bad' | 'idle';

const TONE: Record<Tone, { tw: string; bar: string; border: string }> = {
  good: { tw: 'text-emerald-400', bar: 'bg-emerald-400', border: 'border-emerald-500/40' },
  warn: { tw: 'text-amber-400',   bar: 'bg-amber-400',   border: 'border-amber-500/40' },
  bad:  { tw: 'text-red-400',     bar: 'bg-red-400',     border: 'border-red-500/40' },
  idle: { tw: 'text-slate-500',   bar: 'bg-slate-600',   border: 'border-slate-700' },
};

function readinessTone(v: number | null): Tone {
  if (v == null || v <= 0) return 'idle';
  if (v >= 70) return 'good';
  if (v >= 40) return 'warn';
  return 'bad';
}

function readinessWord(v: number | null): string {
  if (v == null || v <= 0) return 'NO SIGNAL';
  if (v >= 70) return 'OPTIMIZED';
  if (v >= 40) return 'MODERATE';
  return 'DEGRADED';
}

interface Vital { label: string; value: string; sub: string; tone: Tone; Icon: typeof Activity }

export default function BiohackerDashboard({ data }: { data: DashboardData }) {
  const { activeBreak } = data;
  const ir = data.intelligenceReport;
  const cs = ir?.currentState;
  const bm = ir?.extended?.biometrics ?? null;
  const es = ir?.extended?.energySummary ?? null;
  const ns = ir?.extended?.nutritionStatus ?? null;
  const ta = data.todayActivity;

  // Training-stress balance — loaded once on mount (CardiacAge + ActionPoints
  // self-load their own data).
  const [formCurve, setFormCurve] = useState<FormCurveSummary | null>(null);
  const [deepLoaded, setDeepLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ getSessionHistory }, { computeFormCurve }] = await Promise.all([
          import('@/lib/sessionStorage'),
          import('@/lib/formCurve'),
        ]);
        const sessions = await getSessionHistory({ limit: 200 });
        if (cancelled) return;
        setFormCurve(computeFormCurve(sessions, 84));
      } catch (e) {
        console.warn('[biohacker] deep panel load failed:', e);
      } finally {
        if (!cancelled) setDeepLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [data.user?.uid]);

  // See displayReadiness — null when the number is the provisional constant.
  const readiness = displayReadiness(data.intelligenceReport);
  const rTone = readinessTone(readiness);
  const rt = TONE[rTone];

  // ── Recovery quad — each read against its own baseline ──
  const hrvVal = bm?.hrvCurrent ?? (ta?.hrv && ta.hrv > 0 ? ta.hrv : null);
  const hrv: Vital = (() => {
    if (hrvVal == null) return { label: 'HRV', value: '—', sub: 'no signal', tone: 'idle', Icon: Activity };
    const base = bm?.hrvBaseline ?? null;
    const delta = base != null ? Math.round(hrvVal - base) : null;
    const tone: Tone = bm?.hrvStatus === 'suppressed' ? 'warn' : (delta != null && delta < 0 ? 'warn' : 'good');
    return { label: 'HRV', value: `${Math.round(hrvVal)}`, sub: delta != null ? `${delta >= 0 ? '+' : ''}${delta} ms vs base` : 'ms', tone, Icon: Activity };
  })();

  const rhrVal = bm?.rhrCurrent ?? (ta?.restingHeartRate && ta.restingHeartRate > 0 ? ta.restingHeartRate : null);
  const rhr: Vital = (() => {
    if (rhrVal == null) return { label: 'RHR', value: '—', sub: 'no signal', tone: 'idle', Icon: HeartPulse };
    const base = bm?.rhrBaseline ?? null;
    const delta = base != null ? Math.round(rhrVal - base) : null;
    // Lower RHR is better — elevated vs baseline is the warning direction.
    const tone: Tone = bm?.rhrStatus === 'elevated' ? 'bad'
      : bm?.rhrStatus === 'suppressed' ? 'good'
      : (delta != null && delta > 3 ? 'warn' : 'good');
    return { label: 'RHR', value: `${Math.round(rhrVal)}`, sub: delta != null ? `${delta >= 0 ? '+' : ''}${delta} bpm vs base` : 'bpm', tone, Icon: HeartPulse };
  })();

  const sleepMin = bm?.sleepMinutes ?? (ta?.sleepDurationMinutes && ta.sleepDurationMinutes > 0 ? ta.sleepDurationMinutes : null);
  const sleep: Vital = (() => {
    if (sleepMin == null || sleepMin <= 0) return { label: 'Sleep', value: '—', sub: 'no signal', tone: 'idle', Icon: Moon };
    const h = sleepMin / 60;
    const tone: Tone = h >= 7 ? 'good' : h >= 6 ? 'warn' : 'bad';
    return { label: 'Sleep', value: `${h.toFixed(1)}h`, sub: tone === 'good' ? 'rested' : 'short', tone, Icon: Moon };
  })();

  const neatScore = es?.hasWearableData ? es.neatScore : null;
  const stepsVal = es?.steps ?? ta?.steps ?? null;
  const neat: Vital = (() => {
    if (neatScore != null && neatScore > 0) {
      const tone: Tone = neatScore >= 70 ? 'good' : neatScore >= 40 ? 'warn' : 'bad';
      return { label: 'NEAT', value: `${Math.round(neatScore)}`, sub: stepsVal ? `${Math.round(stepsVal).toLocaleString()} steps` : 'score', tone, Icon: Footprints };
    }
    if (stepsVal != null && stepsVal > 0) {
      const goal = es?.stepGoal || 8000;
      const tone: Tone = stepsVal >= goal ? 'good' : stepsVal >= goal * 0.6 ? 'warn' : 'bad';
      return { label: 'Steps', value: stepsVal >= 1000 ? `${(stepsVal / 1000).toFixed(1)}k` : `${stepsVal}`, sub: stepsVal >= goal ? 'goal ✓' : `/ ${(goal / 1000).toFixed(0)}k`, tone, Icon: Footprints };
    }
    return { label: 'NEAT', value: '—', sub: 'no signal', tone: 'idle', Icon: Footprints };
  })();

  const quad: Vital[] = [hrv, rhr, sleep, neat];
  const hasAnyVital = quad.some(v => v.value !== '—');

  // Subjective fallback line when no wearable vitals exist at all.
  const subj = cs?.todaySubjective;
  const fallbackLine = subj
    ? `Check-in — energy ${subj.energy}/10 · sleep ${subj.sleepHours}h · stress ${subj.stress}/10`
    : 'Connect a wearable (HRV / RHR / sleep) or log a check-in to calibrate your systems';

  // Nutrition precision line.
  const proteinPerLb = ns?.proteinPerLbBodyweight ?? null;
  const calBalance = ns && (ns.daysTracked ?? 0) > 0 ? ns.calorieBalance : null;
  const showNutrition = proteinPerLb != null || calBalance != null;

  return (
    <div className="space-y-3">
      {/* ── BIO-SYSTEMS HERO ── composite readiness + recovery quad */}
      <section className={`relative overflow-hidden rounded-xl border ${rt.border} bg-hud-ink/85`}>
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
        <header className="px-4 py-1.5 border-b border-slate-800/60 font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500 flex items-center gap-2">
          <Beaker className="w-3 h-3 text-hud-cyan/70" />
          <span>Bio-Systems · Quantified Self · SYS·BIO·01</span>
        </header>

        {/* Readiness headline */}
        <div className="px-4 py-3 flex items-center gap-4 border-b border-slate-800/50">
          <span className={`w-3 h-3 rounded-full ${rt.bar} animate-hud-blink shrink-0`} aria-hidden />
          <div className="min-w-0 flex-1">
            <div className={`font-heading text-2xl font-bold tracking-wide ${rt.tw}`}>{readinessWord(readiness)}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">
              Readiness · composite of recovery systems
            </div>
          </div>
          {readiness != null && readiness > 0 && (
            <div className="text-right shrink-0">
              <div className={`font-heading text-3xl font-bold tabular-nums ${rt.tw}`}>{Math.round(readiness)}</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">/ 100</div>
            </div>
          )}
        </div>

        {/* Recovery quad — telemetry cells */}
        {hasAnyVital ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-800/50">
            {quad.map((v) => {
              const t = TONE[v.tone];
              const Icon = v.Icon;
              return (
                <button
                  key={v.label}
                  onClick={() => { window.location.href = '/recovery'; }}
                  className="bg-hud-ink/80 px-3 py-2.5 flex flex-col gap-1 text-left hover:bg-hud-ink/95 transition"
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3 h-3 ${t.tw}`} aria-hidden />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{v.label}</span>
                  </div>
                  <div className={`font-heading text-lg font-bold tabular-nums leading-none ${t.tw}`}>{v.value}</div>
                  <div className="font-mono text-[9px] text-slate-500 truncate">{v.sub}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <button
            onClick={() => { window.location.href = '/recovery'; }}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-hud-ink/95 transition"
          >
            <Activity className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden />
            <span className="text-[11px] text-slate-400 flex-1 leading-snug">{fallbackLine}</span>
            <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
          </button>
        )}
      </section>

      {/* Start workout CTA */}
      {!activeBreak && <StartWorkoutCard data={data} />}

      {/* ── OPTIMIZATION ── the levers a quantified-self user tunes */}
      <div className="pt-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500 px-1 mb-1.5">
          Optimization
        </div>
        <div className="space-y-2.5">
          {/* Cross-domain action points — self-loads */}
          {data.user?.uid && <ActionPoints userId={data.user.uid} />}

          {/* Nutrition precision — protein/lb floor + energy balance */}
          {showNutrition && (
            <button
              onClick={() => { window.location.href = '/fuel'; }}
              className="w-full flex items-center gap-3 rounded-xl border border-slate-800/80 bg-hud-ink/70 px-3.5 py-2.5 text-left hover:bg-hud-ink/90 transition"
            >
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Protein</div>
                  <div className={`font-heading text-base font-bold tabular-nums ${proteinPerLb != null && proteinPerLb >= 0.8 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {proteinPerLb != null ? `${proteinPerLb.toFixed(2)} g/lb` : '—'}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Cal balance</div>
                  <div className="font-heading text-base font-bold tabular-nums text-slate-200">
                    {calBalance != null ? `${calBalance > 0 ? '+' : ''}${Math.round(calBalance)}` : '—'}
                  </div>
                </div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
            </button>
          )}

          {/* Training-stress balance — fitness vs fatigue vs form */}
          {deepLoaded && <FormCurveCard summary={formCurve} />}

          {/* Cardiac age — self-loads + self-gates on confidence */}
          <CardiacAgeCard />
        </div>
      </div>
    </div>
  );
}
